import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Button } from "PFComponents/button";
import { LoadingDots } from "PFComponents/loading_dots";
import { Modal } from "PFComponents/modal";
import { getProfileName, isSaasAdmin } from "PFCore/helpers/profile";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useCurrentAccount } from "PFCore/hooks/queries/account";
import { useDeleteUser } from "PFCore/hooks/queries/admin/use_delete_user";
import { useUser } from "PFCore/hooks/queries/admin/use_user";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { resetRegistrationStatus } from "PFCore/services/admin/users/reset_registration_status";
import { sendWelcomeEmails } from "PFCore/services/admin/users/send_welcome_emails";
import { forgotPassword } from "PFCore/services/signup/forgot_password";
import { FeatureFlag, Profile } from "PFTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import css from "./admin_info.module.scss";

const renderPair = (headerText: string, value: React.ReactNode) => (
  <tr>
    <th>{headerText}</th>
    <td style={{ paddingLeft: 10 }}>{value}</td>
  </tr>
);

type AdminInfoProps = {
  profile: Profile;
};

export const AdminInfo = ({ profile }: AdminInfoProps) => {
  const { data: currentAccount } = useCurrentAccount();
  const { data: currentProfile } = useCurrentProfile();
  const { showGrowl } = useGrowlStoreActions();
  const { t } = useTranslation("profiles", { keyPrefix: "edit.sections.admin.info" });
  const { t: tTranslation } = useTranslation();
  const isEmailsEnabled = useIsFeatureEnabled()(FeatureFlag.Emails);

  const { data: queryUser, isFetched } = useUser(String(profile.id));
  const navigate = useNavigate();
  const { mutate: deleteUser } = useDeleteUser(String(profile.id), {
    onSuccess: (resp) => {
      showGrowl({ message: resp.message });
      navigate("/admin");
    }
  });

  const [displayRemoveConfirm, setDisplayRemoveConfirm] = useState(false);
  const [displayResetPasswordConfirm, setDisplayResetPasswordConfirm] = useState(false);
  const [displayResetRegistrationConfirm, setDisplayResetRegistrationConfirm] = useState(false);
  const [displayResendWelcomeConfirm, setDisplayResendWelcomeConfirm] = useState(false);

  const handleResetPassword = () => {
    if (!currentAccount) {
      return;
    }
    forgotPassword({ accountId: currentAccount.id, email: profile.email }, !isEmailsEnabled).then(() =>
      showGrowl({ message: t("resetPassword.successMessage") })
    );
  };

  const handleResetRegistration = () => {
    resetRegistrationStatus(String(profile.id)).then(
      () => showGrowl({ message: t("resetRegistration.successMessage") }),
      () =>
        showGrowl({
          message: t("resetRegistration.errorMessage"),
          kind: "error"
        })
    );
  };

  const handleResendWelcome = () => {
    sendWelcomeEmails([String(profile.id)], !isEmailsEnabled).then(
      () => showGrowl({ message: t("welcomeEmails.successMessage") }),
      () =>
        showGrowl({
          message: t("welcomeEmails.errorMessage"),
          kind: "error"
        })
    );
  };

  const canAlter = isSaasAdmin(currentProfile) || !isSaasAdmin(profile);
  const canDelete = isSaasAdmin(currentProfile) && currentProfile?.id !== profile.id;

  const displayName = getProfileName(profile) || profile.email;

  return (
    <>
      <div style={{ marginBottom: 30 }}>
        <div className={css.alertButtons}>
          {canAlter && (
            <>
              <Button
                kind="secondary"
                qaId="resetPassword"
                onClick={() => setDisplayResetPasswordConfirm(true)}
              >
                {t("resetPassword.buttonText")}
              </Button>
              <Button
                kind="secondary"
                qaId="resetRegistration"
                onClick={() => setDisplayResetRegistrationConfirm(true)}
              >
                {t("resetRegistration.buttonText")}
              </Button>
              <Button
                kind="secondary"
                qaId="sendWelcome"
                onClick={() => setDisplayResendWelcomeConfirm(true)}
              >
                {t("welcomeEmails.buttonText")}
              </Button>
            </>
          )}
          {canDelete && (
            <Button kind="danger" onClick={() => setDisplayRemoveConfirm(true)}>
              {tTranslation("remove")}
            </Button>
          )}
        </div>
        {!isFetched && <LoadingDots />}
        {isFetched && (
          <table>
            <tbody>{renderPair(t("messagesSent"), queryUser?.statistics?.messagesSentCount)}</tbody>
          </table>
        )}
      </div>
      {displayRemoveConfirm && (
        <Modal
          title={t("deleteUser.title")}
          onOK={deleteUser}
          onClose={() => setDisplayRemoveConfirm(false)}
          kindOK="danger"
        >
          {tTranslation("sureToDelete", { key: displayName })}
        </Modal>
      )}
      {displayResetPasswordConfirm && (
        <Modal
          title={t("resetPassword.title")}
          onOK={handleResetPassword}
          onClose={() => setDisplayResetPasswordConfirm(false)}
        >
          {t(isEmailsEnabled ? "resetPassword.confirmMessage" : "resetPassword.confirmMessageForce", {
            name: displayName
          })}
        </Modal>
      )}
      {displayResetRegistrationConfirm && (
        <Modal
          title={t("resetRegistration.title")}
          onOK={handleResetRegistration}
          onClose={() => setDisplayResetRegistrationConfirm(false)}
        >
          {t("resetRegistration.confirmMessage", { name: displayName })}
        </Modal>
      )}
      {displayResendWelcomeConfirm && (
        <Modal
          title={t("welcomeEmails.title")}
          onOK={handleResendWelcome}
          onClose={() => setDisplayResendWelcomeConfirm(false)}
        >
          {t(isEmailsEnabled ? "welcomeEmails.confirmMessage" : "welcomeEmails.confirmMessageForce", {
            name: displayName
          })}
        </Modal>
      )}
    </>
  );
};
